import { PROVIDER_IDS, type ProviderId } from "./modelChoice.js";

/**
 * Somebody's own model API keys, at most one per provider, kept where they
 * chose to keep them. Today that is one place: **this tab**, in
 * sessionStorage, gone when the tab closes, on sign out, and when another
 * account signs in here. They are added and forgotten in Settings.
 *
 * The key is theirs and is billed to them. It is only ever sent to its
 * provider, from this browser (`anthropicModel.ts`, `geminiModel.ts`): it never
 * reaches utucat's worker, so nothing on the server can leak it, and
 * `lib/api.ts` does not import this file. See docs/notes/decisions.md.
 *
 * Where a key lives is behind `KeyStore` because a second home is planned: an
 * opt-in, off-by-default "keep it with my account", encrypted on the server so
 * it follows somebody between devices (the roadmap's backlog says what that
 * needs). That one would be another `KeyStore`, chosen by the person, with this
 * one still the default. Not localStorage: a key that outlives the tab on a
 * shared machine is the risk this design exists to avoid.
 */

/** At most one key per provider. */
export type StoredKeys = Partial<Record<ProviderId, string>>;

/** Where keys are kept. Only "tab" exists; "account" is the planned opt-in. */
export type KeyHome = "tab";

export interface KeyStore {
  readonly home: KeyHome;
  read(): StoredKeys;
  save(provider: ProviderId, key: string): void;
  /** One provider's key, or every key when none is named. */
  forget(provider?: ProviderId): void;
}

const KEY = "iltocat.modelKey";

/** sessionStorage can be refused (a locked-down browser); then keys last only as long as this page. */
let fallback: StoredKeys = {};

function parse(raw: string | null): StoredKeys | null {
  if (!raw) return null;
  try {
    const value = JSON.parse(raw) as Record<string, unknown>;
    const keys: StoredKeys = {};
    for (const id of PROVIDER_IDS) if (typeof value[id] === "string") keys[id] = value[id];
    // One key with its provider, as the first version with Gemini kept it.
    if (typeof value["key"] === "string" && PROVIDER_IDS.includes(value["provider"] as ProviderId)) {
      keys[value["provider"] as ProviderId] = value["key"];
    }
    return keys;
  } catch {
    // A bare key from before there was a choice of provider: those were all Anthropic's.
    return raw.startsWith("sk-ant-") ? { anthropic: raw } : null;
  }
}

function write(keys: StoredKeys): void {
  fallback = keys;
  try {
    if (Object.keys(keys).length === 0) window.sessionStorage.removeItem(KEY);
    else window.sessionStorage.setItem(KEY, JSON.stringify(keys));
  } catch {
    // Kept in memory instead, which is stricter than asked for.
  }
}

export const tabKeyStore: KeyStore = {
  home: "tab",
  read() {
    try {
      return parse(window.sessionStorage.getItem(KEY)) ?? fallback;
    } catch {
      return fallback;
    }
  },
  save(provider, key) {
    write({ ...tabKeyStore.read(), [provider]: key });
  },
  forget(provider) {
    if (!provider) return write({});
    const keys = { ...tabKeyStore.read() };
    delete keys[provider];
    write(keys);
  },
};

/** Called on sign-out and when another account signs in on this device. */
export function forgetModelKey(): void {
  tabKeyStore.forget();
}

/**
 * What is wrong with a pasted key, or null. Only the shape: whether the
 * provider accepts it is the provider's to say, when the key is checked.
 * Google's keys have changed format (`AIza…`, now `AQ.…`), so theirs are
 * not held to a prefix.
 */
export function keyProblem(provider: ProviderId, raw: string): string | null {
  const key = raw.trim();
  if (!key) return "Paste your key first.";
  if (/\s/.test(key)) return "That key has a space in it. Copy it again from where you made it.";
  if (provider === "anthropic") {
    if (!key.startsWith("sk-ant-"))
      return "That does not look like an Anthropic key. They start with sk-ant-.";
    if (key.startsWith("sk-ant-admin")) {
      return "That is an Admin key, which can manage your whole organisation. Make an ordinary API key for this instead.";
    }
  } else if (key.startsWith("sk-")) {
    return "That looks like an Anthropic or OpenAI key, not a Google one. Choose the provider it is from.";
  }
  return null;
}

/** Enough of a key to tell two apart, and not enough to use. */
export function maskKey(key: string): string {
  return key.length > 14 ? `${key.slice(0, 6)}…${key.slice(-4)}` : "…";
}
